import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Copy, Upload, Gift } from "lucide-react";
import { StarterIngredientCategoryCombobox } from "@/components/StarterIngredientCategoryCombobox";
import { Pagination } from "@/components/Pagination";
import { formatCurrency } from "@/lib/currency";
import type { StarterIngredient, StarterIngredientCategory } from "@/lib/types";

interface StarterIngredientsTabProps {
  starterIngredients: StarterIngredient[] | undefined;
  ingredientsLoading: boolean;
  starterIngredientCategories: any[];
  currency: string;
  isAdmin: boolean;
  ingredientPage: number;
  setIngredientPage: (page: number) => void;
  ingredientSortField: string;
  setIngredientSortField: (field: string) => void;
  ingredientSortDirection: "asc" | "desc";
  setIngredientSortDirection: (dir: "asc" | "desc") => void;
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  onEdit: (item: StarterIngredient) => void;
  onDelete: (id: string) => void;
  onImport: (id: string) => void;
  onImportAll: () => void;
  onUpload: () => void;
  onAdd: () => void;
  onView?: (item: StarterIngredient) => void;
  importIngredientMutation: any;
  importAllIngredientsMutation: any;
  deleteIngredientMutation: any;
}

export function StarterIngredientsTab({
  starterIngredients,
  ingredientsLoading,
  starterIngredientCategories,
  currency,
  isAdmin,
  ingredientPage,
  setIngredientPage,
  ingredientSortField,
  setIngredientSortField,
  ingredientSortDirection,
  setIngredientSortDirection,
  itemsPerPage,
  setItemsPerPage,
  onEdit,
  onDelete,
  onImport,
  onImportAll,
  onUpload,
  onAdd,
  onView,
  importIngredientMutation,
  importAllIngredientsMutation,
  deleteIngredientMutation,
}: StarterIngredientsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const getColorFromName = (name: string) => {
    const colors = [
      'bg-red-200', 'bg-blue-200', 'bg-green-200', 'bg-yellow-200',
      'bg-purple-200', 'bg-pink-200', 'bg-indigo-200', 'bg-orange-200'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Sorting logic
  const sortedIngredients = useMemo(() => {
    if (!starterIngredients) return [];

    // Filter by search query
    const filtered = starterIngredients.filter(ing =>
      ing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (starterIngredientCategories?.find(c => c._id === ing.categoryId)?.name || ing.category || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[ingredientSortField as keyof typeof a];
      let bVal = b[ingredientSortField as keyof typeof b];

      if (ingredientSortField === 'category') {
        aVal = starterIngredientCategories?.find(c => c._id === a.categoryId)?.name || a.category || '';
        bVal = starterIngredientCategories?.find(c => c._id === b.categoryId)?.name || b.category || '';
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return ingredientSortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const aNum = parseFloat(aVal as string) || 0;
      const bNum = parseFloat(bVal as string) || 0;
      return ingredientSortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return sorted;
  }, [starterIngredients, ingredientSortField, ingredientSortDirection, starterIngredientCategories, searchQuery]);

  const paginatedIngredients = useMemo(() => {
    if (!sortedIngredients) return [];
    const start = (ingredientPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedIngredients.slice(start, end);
  }, [sortedIngredients, ingredientPage, itemsPerPage]);

  const ingredientTotalPages = Math.max(1, Math.ceil((sortedIngredients?.length || 0) / itemsPerPage));

  const handleSort = (field: string) => {
    if (ingredientSortField === field) {
      setIngredientSortDirection(ingredientSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setIngredientSortField(field);
      setIngredientSortDirection('asc');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 space-y-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Starter Ingredients ({sortedIngredients?.length || 0})</CardTitle>
        <div className="flex gap-2">
          {!isAdmin && starterIngredients && starterIngredients.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onImportAll}
              disabled={importAllIngredientsMutation.isPending}
            >
              <Gift className="h-4 w-4 mr-2" />
              {importAllIngredientsMutation.isPending ? "Importing..." : "Import All"}
            </Button>
          )}
          {isAdmin && (
            <>
              <Button variant="default" size="sm" onClick={onAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Ingredient
              </Button>
              <Button variant="outline" onClick={onUpload}>
                <Upload className="h-4 w-4 mr-2" />
                Upload CSV
              </Button>
            </>
          )}
        </div>
        </div>
        <Input
            placeholder="Search ingredients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
      </CardHeader>
      <CardContent>
        {ingredientsLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : starterIngredients && starterIngredients.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('name')}
                  >
                    Name {ingredientSortField === 'name' && (ingredientSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('category')}
                  >
                    Category {ingredientSortField === 'category' && (ingredientSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('purchaseAmount')}
                  >
                    Purchase Amount {ingredientSortField === 'purchaseAmount' && (ingredientSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('pricePerGram')}
                  >
                    Price/g {ingredientSortField === 'pricePerGram' && (ingredientSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedIngredients.map((item) => (
                  <TableRow
                    key={item._id || item.id}
                    onClick={() => onView?.(item)}
                    className={onView ? "cursor-pointer hover:bg-muted/50" : ""}
                  >
                    <TableCell>
                      {(item.image || item.imageUrl) ? (
                        <img src={item.image || item.imageUrl} alt={item.name} className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div className={`w-10 h-10 ${getColorFromName(item.name)} rounded flex items-center justify-center text-xs font-semibold text-gray-700`}>
                          {item.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.name}
                      {item.isCountBased && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">Count</span>
                      )}
                    </TableCell>
                    <TableCell>{starterIngredientCategories?.find(c => c._id === item.categoryId)?.name || item.category || "-"}</TableCell>
                    <TableCell className="text-right">{formatCurrency(parseFloat(item.purchaseAmount || '0'), currency)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(parseFloat(item.pricePerGram || '0'), currency)}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {!isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onImport(item._id || item.id)}
                            disabled={importIngredientMutation.isPending}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </Button>
                        )}
                        {isAdmin && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => onEdit(item)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="ghost">
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Starter Ingredient</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{item.name}"? This will not affect users who have already imported this ingredient.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => {
                                      const itemId = item._id || item.id;
                                      if (!itemId) return;
                                      onDelete(itemId);
                                    }}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={ingredientPage}
              totalPages={ingredientTotalPages}
              totalItems={sortedIngredients?.length || 0}
              itemsPerPage={itemsPerPage}
              onPageChange={setIngredientPage}
              onPageSizeChange={setItemsPerPage}
            />
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p>No starter ingredients yet.</p>
            <p className="text-sm">Add common ingredients that new users can import to get started quickly.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
